import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { Plus, Trash2, X, UserCheck, FileSignature, Printer } from "lucide-react";

function printFichas() {
  const logoUrl = `${window.location.origin}/assets/logo.png`;

  const ficha = () => `
    <div class="ficha">
      <div class="top">
        <img src="${logoUrl}" class="logo" onerror="this.style.display='none'" />
        <div class="top-text">
          <div class="firm">ANA CRISTINA DOMINGUES ADVOGADOS</div>
          <div class="sub">Ficha de Novo Cliente</div>
        </div>
      </div>
      <div class="f"><span>Data da reunião</span><div class="l"></div></div>
      <div class="f"><span>Nome do cliente</span><div class="l"></div></div>
      <div class="f two">
        <span>NIF</span><div class="l short"></div>
        <span class="ml">Telefone / Email</span><div class="l"></div>
      </div>
      <div class="f"><span>Morada</span><div class="l"></div></div>
      <div class="f"><span>Tipo de processo</span><div class="l"></div></div>
      <div class="fb"><span>Descrição do assunto</span>
        <div class="l full"></div>
        <div class="l full"></div>
        <div class="l full"></div>
      </div>
      <div class="fb"><span>Observações</span>
        <div class="l full"></div>
        <div class="l full"></div>
      </div>
      <div class="sig">Assinatura: <div class="l sig-line"></div></div>
    </div>`;

  const html = `<!DOCTYPE html>
<html lang="pt">
<head>
<meta charset="utf-8">
<title>Fichas de Novo Cliente — ACD Advogados</title>
<style>
  @page { size: A4 landscape; margin: 7mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; background: #fff; }
  .page {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: calc(210mm - 14mm);
    gap: 0;
  }
  .cut-line {
    width: 1px;
    background: repeating-linear-gradient(
      to bottom,
      #bbb 0mm, #bbb 2mm,
      transparent 2mm, transparent 4mm
    );
    flex-shrink: 0;
    position: relative;
  }
  .cut-line::before {
    content: '✂';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 8pt;
    color: #aaa;
    background: #fff;
    padding: 1mm 0;
  }
  .ficha {
    flex: 1;
    padding: 5mm 6mm;
    border: 0.3mm solid #ddd;
    border-radius: 2mm;
    margin: 0 1mm;
    display: flex;
    flex-direction: column;
    gap: 2.2mm;
    overflow: hidden;
  }
  .top {
    display: flex;
    align-items: center;
    gap: 3mm;
    padding-bottom: 3mm;
    border-bottom: 0.6mm solid #0b679e;
    flex-shrink: 0;
  }
  .logo { height: 13mm; width: auto; object-fit: contain; flex-shrink: 0; }
  .firm { font-size: 7.5pt; font-weight: bold; color: #0b679e; line-height: 1.4; }
  .sub { font-size: 6pt; color: #888; margin-top: 0.5mm; }
  .f {
    display: flex;
    align-items: flex-end;
    gap: 2mm;
    flex-shrink: 0;
  }
  .f.two { flex-wrap: nowrap; }
  .f span {
    font-size: 6pt;
    color: #444;
    white-space: nowrap;
    line-height: 1;
    flex-shrink: 0;
  }
  .ml { margin-left: 2mm; }
  .l {
    flex: 1;
    border-bottom: 0.4mm solid #333;
    height: 3.8mm;
  }
  .l.short { flex: 0 0 22mm; }
  .fb { flex-shrink: 0; }
  .fb > span {
    font-size: 6pt;
    color: #444;
    display: block;
    margin-bottom: 1mm;
  }
  .l.full {
    display: block;
    width: 100%;
    border-bottom: 0.4mm solid #333;
    height: 4.5mm;
    margin-bottom: 2mm;
  }
  .l.full:last-child { margin-bottom: 0; }
  .sig {
    display: flex;
    align-items: flex-end;
    gap: 2mm;
    font-size: 6pt;
    color: #444;
    margin-top: auto;
    padding-top: 2mm;
    border-top: 0.3mm dashed #ccc;
    flex-shrink: 0;
    white-space: nowrap;
  }
  .sig-line { flex: 1; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
<div class="page">
  ${ficha()}
  <div class="cut-line"></div>
  ${ficha()}
  <div class="cut-line"></div>
  ${ficha()}
</div>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "width=1000,height=700");
  win.focus();
  // Revogar o URL após impressão para libertar memória
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

const EMPTY_FORM = {
  numero_contrato: "",
  nome_cliente: "",
  nif: "",
  morada: "",
  contactos: "",
  valor_contrato: "",
  observacoes: "",
};

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("pt-PT");
}

export default function ClientesPendentes() {
  const [clientes, setClientes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/clientes-pendentes`, { headers: getAuthHeader() });
    setClientes(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Marcar como concluído e remover desta lista?")) return;
    await axios.delete(`${API}/clientes-pendentes/${id}`, { headers: getAuthHeader() });
    load();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome_cliente.trim()) { setErrors({ nome_cliente: "Obrigatório" }); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/clientes-pendentes`, form, { headers: getAuthHeader() });
      setShowModal(false);
      setForm(EMPTY_FORM);
      setErrors({});
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clientes Pendentes</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Clientes aguardando criação de pasta — {clientes.length} pendente{clientes.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="btn-imprimir-fichas"
            onClick={printFichas}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 transition-all"
          >
            <Printer size={15} /> Imprimir Fichas
          </button>
          <button
            data-testid="add-cliente-pendente-btn"
            onClick={() => { setShowModal(true); setForm(EMPTY_FORM); setErrors({}); }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium shadow-sm transition-all hover:opacity-90"
            style={{ backgroundColor: "#0b679e" }}
          >
            <Plus size={16} /> Novo cliente
          </button>
        </div>
      </div>

      {/* Lista */}
      {clientes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <UserCheck size={48} strokeWidth={1.2} className="mb-3 text-slate-300" />
          <p className="text-base font-medium">Sem clientes pendentes</p>
          <p className="text-sm mt-1">Todos os contratos têm pasta criada.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {clientes.map(c => (
            <div
              key={c.id}
              data-testid={`cliente-pendente-${c.id}`}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow"
            >
              {/* Nome + contrato */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 text-base leading-snug truncate">{c.nome_cliente}</h3>
                  {c.numero_contrato && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#0b679e] font-semibold mt-1">
                      <FileSignature size={11} /> Tipo de Contrato: {c.numero_contrato}
                    </span>
                  )}
                </div>
                <button
                  data-testid={`delete-cliente-${c.id}`}
                  onClick={() => handleDelete(c.id)}
                  title="Pasta criada — remover desta lista"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors flex-shrink-0"
                >
                  <UserCheck size={13} /> Pasta criada
                </button>
              </div>

              {/* Dados */}
              <div className="space-y-1.5 text-sm">
                {c.nif && (
                  <div className="flex gap-2">
                    <span className="text-slate-400 w-16 flex-shrink-0">NIF</span>
                    <span className="text-slate-700 font-medium">{c.nif}</span>
                  </div>
                )}
                {c.morada && (
                  <div className="flex gap-2">
                    <span className="text-slate-400 w-16 flex-shrink-0">Morada</span>
                    <span className="text-slate-700">{c.morada}</span>
                  </div>
                )}
                {c.contactos && (
                  <div className="flex gap-2">
                    <span className="text-slate-400 w-16 flex-shrink-0">Contactos</span>
                    <span className="text-slate-700">{c.contactos}</span>
                  </div>
                )}
                {c.valor_contrato && (
                  <div className="flex gap-2">
                    <span className="text-slate-400 w-16 flex-shrink-0">Valor</span>
                    <span className="font-semibold text-[#0b679e]">{c.valor_contrato}</span>
                  </div>
                )}
                {c.observacoes && (
                  <div className="flex gap-2">
                    <span className="text-slate-400 w-16 flex-shrink-0">Notas</span>
                    <span className="text-slate-600 text-xs">{c.observacoes}</span>
                  </div>
                )}
              </div>

              <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-400">
                Adicionado em {formatDate(c.created_at)}{c.created_by_nome ? ` por ${c.created_by_nome}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">Novo Cliente Pendente</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Nome do cliente *</label>
                <input
                  data-testid="input-nome-cliente"
                  value={form.nome_cliente}
                  onChange={e => set("nome_cliente", e.target.value)}
                  className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]/30 ${errors.nome_cliente ? "border-red-400" : "border-slate-200"}`}
                  placeholder="Nome completo"
                />
                {errors.nome_cliente && <p className="text-xs text-red-500 mt-1">{errors.nome_cliente}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de Contrato</label>
                  <input
                    data-testid="input-numero-contrato"
                    value={form.numero_contrato}
                    onChange={e => set("numero_contrato", e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]/30"
                    placeholder="Ex: Avença, Caso, Pro bono..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">NIF</label>
                  <input
                    data-testid="input-nif"
                    value={form.nif}
                    onChange={e => set("nif", e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]/30"
                    placeholder="Ex: 123456789"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Morada</label>
                <input
                  data-testid="input-morada"
                  value={form.morada}
                  onChange={e => set("morada", e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]/30"
                  placeholder="Morada completa"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Contactos</label>
                <input
                  data-testid="input-contactos"
                  value={form.contactos}
                  onChange={e => set("contactos", e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]/30"
                  placeholder="Email, telefone..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Valor do contrato</label>
                <input
                  data-testid="input-valor-contrato"
                  value={form.valor_contrato}
                  onChange={e => set("valor_contrato", e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]/30"
                  placeholder="Ex: 1.500,00 €"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Observações</label>
                <textarea
                  data-testid="input-observacoes"
                  value={form.observacoes}
                  onChange={e => set("observacoes", e.target.value)}
                  rows={3}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]/30 resize-none"
                  placeholder="Notas adicionais..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50">
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="submit-cliente-pendente"
                  disabled={saving}
                  className="px-5 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-60 transition-all hover:opacity-90"
                  style={{ backgroundColor: "#0b679e" }}
                >
                  {saving ? "A guardar..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
